import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Pencil, PiggyBank, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useBudgetsQuery, useDeleteBudgetMutation, useUpsertBudgetMutation } from '@/client/api/business/spend';
import type { Budget } from '@/client/types/business/spend';
import { AccountingPageShell } from './components/AccountingPageShell';
import { Field, inputClass, useSpendLookups } from './components/spend-ui';
import { DeleteConfirmDialog, EmptyState, Panel, ProgressBar, RowMenu } from '../HumanResource/components/hr-ui';
import { money } from '../HumanResource/components/comp-format';

const thisMonth = () => new Date().toISOString().slice(0, 7);
const shift = (month: string, by: number) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return d.toISOString().slice(0, 7);
};
const label = (month: string) => new Date(`${month}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });

type Form = { id?: string; month: string; departmentId: string; categoryId: string; amount: string; currency: string };

export default function BudgetsPage() {
  const [month, setMonth] = useState(thisMonth());
  const budgetsQ = useBudgetsQuery(month, month);
  const lookups = useSpendLookups();
  const upsert = useUpsertBudgetMutation();
  const remove = useDeleteBudgetMutation();
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [form, setForm] = useState<Form | null>(null);
  const [deleting, setDeleting] = useState<Budget | null>(null);

  const data = budgetsQ.data;
  const budgets = data?.budgets ?? [];
  const scope = (b: { departmentId?: string | null; categoryId?: string | null }) =>
    [b.departmentId ? lookups.departmentName(b.departmentId) : 'Whole company', b.categoryId ? lookups.categoryName(b.categoryId) : 'all spend'].join(' · ');

  // Spend no budget line covers.
  const unbudgeted = useMemo(
    () =>
      (data?.spend ?? []).filter(
        (s) =>
          !budgets.some(
            (b) =>
              b.currency === s.currency &&
              (b.departmentId == null || b.departmentId === s.departmentId) &&
              (b.categoryId == null || b.categoryId === s.categoryId),
          ),
      ),
    [data, budgets],
  );

  const over = budgets.filter((b) => b.actual > b.amount).length;
  const stats = [
    { key: 'lines', label: 'Budget lines', value: budgets.length },
    { key: 'over', label: 'Over budget', value: over },
    { key: 'near', label: 'Above 80%', value: budgets.filter((b) => b.actual <= b.amount && b.actual >= b.amount * 0.8).length },
    { key: 'unbudgeted', label: 'Unbudgeted spend lines', value: unbudgeted.length },
  ];

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    upsert.mutate(
      {
        id: form.id,
        month: form.month,
        departmentId: form.departmentId || null,
        categoryId: form.categoryId || null,
        amount: Number(form.amount),
        currency: form.currency.trim().toUpperCase(),
      },
      {
        onSuccess: () => {
          toast.success('Budget saved');
          setForm(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save the budget'),
      },
    );
  };

  return (
    <AccountingPageShell
      mock={false}
      title="Budgets"
      description="A monthly limit for the company, a department, a category, or both. Actual spend is approved and paid bills plus approved and reimbursed expenses."
      crumbs={[{ label: 'Accounting', href: '/admin/accounting' }, { label: 'Budgets' }]}
      icon={PiggyBank}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMonth(shift(month, -1))}>
            ←
          </Button>
          <span className="w-36 text-center text-sm font-medium text-gray-700">{label(month)}</span>
          <Button variant="outline" size="sm" onClick={() => setMonth(shift(month, 1))}>
            →
          </Button>
          <PermissionButton
            code={PERMISSIONS.accounting.manage}
            className="gap-2"
            onClick={() => setForm({ month, departmentId: '', categoryId: '', amount: '', currency: 'GHS' })}
          >
            <Plus className="h-4 w-4" /> Add budget
          </PermissionButton>
        </div>
      }
      stats={stats}
    >
      {budgetsQ.isError ? (
        <EmptyState label={`Could not load budgets: ${(budgetsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : budgetsQ.isLoading ? (
        <EmptyState label="Loading budgets…" />
      ) : (
        <div className="space-y-4">
          {budgets.length === 0 ? (
            <EmptyState label={`No budgets for ${label(month)}.`} />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Covers</th>
                    <th className="px-4 py-3 font-medium">Budget</th>
                    <th className="px-4 py-3 font-medium">Spent</th>
                    <th className="px-4 py-3 font-medium">Left</th>
                    <th className="px-4 py-3 font-medium">Used</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {budgets.map((b) => {
                    const pct = b.amount ? (b.actual / b.amount) * 100 : 0;
                    return (
                      <tr key={b.id}>
                        <td className="px-4 py-3 text-gray-900">{scope(b)}</td>
                        <td className="px-4 py-3 text-gray-600">{money(b.amount, b.currency)}</td>
                        <td className="px-4 py-3 text-gray-900">{money(b.actual, b.currency)}</td>
                        <td className={`px-4 py-3 ${b.actual > b.amount ? 'font-medium text-red-600' : 'text-gray-600'}`}>
                          {money(b.amount - b.actual, b.currency)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ProgressBar value={pct} className={`w-28 ${pct > 100 ? '[&>div]:bg-red-500' : ''}`} />
                            <span className="text-xs text-gray-500">{Math.round(pct)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <RowMenu
                            items={[
                              {
                                label: 'Edit',
                                icon: Pencil,
                                hidden: !canManage,
                                onSelect: () =>
                                  setForm({ id: b.id, month: b.month, departmentId: b.departmentId ?? '', categoryId: b.categoryId ?? '', amount: String(b.amount), currency: b.currency }),
                              },
                              {
                                label: 'Copy to next month',
                                icon: Copy,
                                hidden: !canManage,
                                onSelect: () =>
                                  setForm({ month: shift(b.month, 1), departmentId: b.departmentId ?? '', categoryId: b.categoryId ?? '', amount: String(b.amount), currency: b.currency }),
                              },
                              { label: 'Delete', icon: Trash2, danger: true, hidden: !canManage, onSelect: () => setDeleting(b) },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {unbudgeted.length > 0 && (
            <Panel title="Spend with no budget">
              <ul className="divide-y divide-gray-50">
                {unbudgeted.map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="text-gray-700">{scope(s)}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-900">{money(s.amount, s.currency)}</span>
                      {canManage && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setForm({ month, departmentId: s.departmentId ?? '', categoryId: s.categoryId ?? '', amount: '', currency: s.currency })}
                        >
                          Budget it
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{form?.id ? 'Edit budget' : 'Add budget'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <Field label="Month">
                <input type="month" required value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className={inputClass} />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Department">
                  <select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })} className={inputClass}>
                    <option value="">Whole company</option>
                    {lookups.departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Category">
                  <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className={inputClass}>
                    <option value="">All spend</option>
                    {lookups.categories.filter((c) => c.status === 'active').map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Amount">
                  <input required type="number" min={0} step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Currency">
                  <input required maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputClass} />
                </Field>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  {upsert.isPending ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this budget line?"
        description={deleting ? `${scope(deleting)}, ${label(deleting.month)}: ${money(deleting.amount, deleting.currency)}. Spend is not affected.` : ''}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSuccess: () => toast.success('Deleted'), onError: (e) => toast.error((e as Error).message) });
          setDeleting(null);
        }}
      />
    </AccountingPageShell>
  );
}
